import {
  BookingForCalendarRecordOutput,
  BookingRecordDetailsPartner,
  BookingRecordList,
  ChoiceRecordBookingStatus
} from '@navio/nsdk';
import { BookingStatusDto, BookingStatuses } from 'noddi-types';
import { BadgeVariant, NoddiBadge, NoddiTooltip } from 'noddi-ui-web';
import { BOOKING_CANCELLATION_REASON_LABELS } from 'noddi-util';
import type { ReactNode } from 'react';

// Unable to complete related props is not a booking status anymore, it was before but that is legacy
// I think most booking endpoints return the unable to complete fields, but not sure.

type BookingBadgeProps = {
  booking:
    | {
        isFullyUnableToComplete: boolean;
        isPartiallyUnableToComplete: boolean;
        status: BookingStatusDto;
        commentsUnableToCompleteInternal?: string;
        commentsUnableToCompletePublic?: string;
        unableToCompleteCommentsInternal?: string[];
        unableToCompleteCommentsPublic?: string[];
      }
    | { status: BookingStatusDto | ChoiceRecordBookingStatus }
    | BookingRecordDetailsPartner
    | BookingForCalendarRecordOutput
    | BookingRecordList;
  /**
   * Stacks cancellation reasons / unable-to-complete comments under the badge.
   * Turn off in single-line list rows, which surface the reason themselves.
   */
  showNotes?: boolean;
};

// BookingRecordList exposes per-task lists; legacy detail records expose a
// single (always-empty in prod) booking-level string. Normalize to list[str],
// drop empty / whitespace-only entries, and dedupe — workers often paste the
// same comment across all unable tasks on a booking, no point repeating it.
const normalizeComments = (raw: string | string[] | null | undefined): string[] => {
  if (!raw) {
    return [];
  }
  const list = Array.isArray(raw) ? raw : [raw];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const c of list) {
    const trimmed = c.trim();
    if (trimmed.length === 0 || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
};

const getPublicComments = (booking: BookingBadgeProps['booking']): string[] => {
  if ('unableToCompleteCommentsPublic' in booking) {
    return normalizeComments(booking.unableToCompleteCommentsPublic);
  }
  if ('commentsUnableToCompletePublic' in booking) {
    return normalizeComments(booking.commentsUnableToCompletePublic);
  }
  return [];
};

const getInternalComments = (booking: BookingBadgeProps['booking']): string[] => {
  if ('unableToCompleteCommentsInternal' in booking) {
    return normalizeComments(booking.unableToCompleteCommentsInternal);
  }
  if ('commentsUnableToCompleteInternal' in booking) {
    return normalizeComments(booking.commentsUnableToCompleteInternal);
  }
  return [];
};

/** Shared copy for drop-in tooltips and filter descriptions. */
export const DROP_IN_DESCRIPTION =
  'Walk-in visit recorded at the shop. It sits outside bookable time: no slot is reserved and it does not reduce availability.';

const isDropInBooking = (booking: BookingBadgeProps['booking']): boolean =>
  'isDropIn' in booking && booking.isDropIn === true;

type Note = { text: string; isInternal: boolean };

const getUnableToCompleteNotes = (booking: BookingBadgeProps['booking']): Note[] => [
  ...getPublicComments(booking).map((text) => ({ text, isInternal: false })),
  ...getInternalComments(booking).map((text) => ({ text, isInternal: true }))
];

const getCancellationNotes = (booking: BookingBadgeProps['booking']): Note[] => {
  if (
    booking.status.label !== BookingStatuses.Cancelled ||
    !('cancellationReason' in booking) ||
    !booking.cancellationReason
  ) {
    return [];
  }
  const label = BOOKING_CANCELLATION_REASON_LABELS[booking.cancellationReason] || booking.cancellationReason;
  return [{ text: label, isInternal: false }];
};

export const BookingBadge = ({ booking, showNotes = true }: BookingBadgeProps) => {
  const bookingStatus = booking.status;
  const dropInBadge = isDropInBooking(booking) ? (
    <NoddiTooltip content={DROP_IN_DESCRIPTION} trigger={<NoddiBadge variant='outline'>Drop-in</NoddiBadge>} />
  ) : null;

  // Check if booking has unable to complete fields
  const hasUnableToCompleteFields = 'isFullyUnableToComplete' in booking && 'isPartiallyUnableToComplete' in booking;

  if (hasUnableToCompleteFields && (booking.isFullyUnableToComplete || booking.isPartiallyUnableToComplete)) {
    return (
      <BadgeWithNotes notes={showNotes ? getUnableToCompleteNotes(booking) : []}>
        <div className='flex flex-wrap items-center gap-1'>
          {booking.isFullyUnableToComplete && <NoddiBadge variant='warning'>Unable to complete</NoddiBadge>}
          {booking.isPartiallyUnableToComplete && (
            <NoddiBadge variant='warning'>Partially unable to complete</NoddiBadge>
          )}
          {dropInBadge}
        </div>
      </BadgeWithNotes>
    );
  }

  const statusBadge = (
    // todo : bug in ts package..
    <div className='flex flex-wrap items-center gap-1'>
      <NoddiBadge variant={getBookingBadgeVariant(bookingStatus)}>
        {getBookingStatusTranslation(bookingStatus)}
      </NoddiBadge>
      {dropInBadge}
    </div>
  );
  const cancellationNotes = showNotes ? getCancellationNotes(booking) : [];

  return cancellationNotes.length > 0 ? (
    <BadgeWithNotes notes={cancellationNotes}>{statusBadge}</BadgeWithNotes>
  ) : (
    statusBadge
  );
};

const BadgeWithNotes = ({ notes, children }: { notes: Note[]; children: ReactNode }) => (
  <div className='flex flex-col gap-1.5'>
    <div>{children}</div>
    {notes.length > 0 && (
      <div className='flex flex-col gap-1'>
        {notes.map((note, i) =>
          note.isInternal ? (
            <p key={i} className='text-xs leading-snug text-gray-500 italic'>
              Internal: {note.text}
            </p>
          ) : (
            <p key={i} className='text-xs leading-snug text-gray-700'>
              {note.text}
            </p>
          )
        )}
      </div>
    )}
  </div>
);

const getBookingBadgeVariant = (status: BookingStatusDto): BadgeVariant => {
  switch (status.label) {
    case BookingStatuses.Completed:
      return 'success';
    case BookingStatuses.Cancelled:
      return 'destructive';
    case BookingStatuses.Assigned:
      return 'outline';
    case BookingStatuses.Confirmed:
      return 'info';
    case BookingStatuses.Draft:
      return 'outline';
    case BookingStatuses['Draft (awaiting payment)']:
    case BookingStatuses['Payment requested']:
      return 'warning';
    default:
      return 'outline';
  }
};

const getBookingStatusTranslation = (status: BookingStatusDto) => {
  switch (status.label) {
    case BookingStatuses.Completed:
      return 'Completed';
    case BookingStatuses.Cancelled:
      return 'Cancelled';
    case BookingStatuses['Assigned']:
      return 'Assigned';
    case BookingStatuses.Confirmed:
      return 'Confirmed';
    case BookingStatuses.Draft:
      return 'Draft';
    case BookingStatuses['Draft (awaiting payment)']:
      return 'Draft (awaiting payment)';
    case BookingStatuses['Payment requested']:
      return 'Payment requested';
    default:
      return 'Unknown';
  }
};
